import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Button, ButtonGroup, Paper, Typography } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { CompareApprovalsProps } from '../types';
import { useCompareApprovals } from '../hooks';
import { ApprovalTermsTable, ConditionsSection } from '../components';
import FileUploadModal from '../../../pages/VendorOpportunity/LenderSubmissionFileUploadModal';

export const CompareApprovals: React.FC<CompareApprovalsProps> = ({ vo, voId, dynamicsVoId, prescreenContent }) => {
  const { activeTab, setActiveTab, approvals, badgesByRow, loading } = useCompareApprovals(dynamicsVoId);
  const [uploadOpen, setUploadOpen] = useState(false);
  const history = useHistory();

  if (loading || (approvals.length === 0 && prescreenContent === undefined)) {
    return null;
  }

  return (
    <Paper sx={{ p: 3 }}>
      {/* Header: Tabs + Upload button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 2,
        }}
      >
        <ButtonGroup
          variant="outlined"
          size="small"
          sx={{
            borderRadius: '8px',
            overflow: 'hidden',
            '& .MuiButtonGroup-grouped': {
              minWidth: 150,
              fontWeight: 600,
              textTransform: 'uppercase',
              fontSize: '0.8rem',
              py: 1,
              px: 2.5,

              borderColor: 'primary.main',
            },
          }}
        >
          {approvals.length > 0 && (
            <Button
              variant={activeTab === 'approvals' ? 'contained' : 'outlined'}
              onClick={() => setActiveTab('approvals')}
              sx={{ ml: 1 }}
            >
              Approvals
            </Button>
          )}
          {prescreenContent !== undefined && (
            <Button
              variant={activeTab === 'prescreen' ? 'contained' : 'outlined'}
              onClick={() => setActiveTab('prescreen')}
              sx={{ mr: 1 }}
            >
              Prescreen Results
            </Button>
          )}
        </ButtonGroup>

        <Button
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          size="small"
          disabled={!vo}
          onClick={() => setUploadOpen(true)}
        >
          Upload Decision Letter
        </Button>
      </Box>

      {vo && (
        <FileUploadModal
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          vo={vo}
          voId={voId}
          relatedTo="submission"
          fixedDocType="decision letter"
          aiEnabledParsing={true}
        />
      )}

      {/* Tab Content */}
      {activeTab === 'approvals' && (
        <Box>
          <Typography variant="caption" fontSize="20px" fontWeight={700} sx={{ mb: 2 }}>
            Compare Approvals
          </Typography>

          <ApprovalTermsTable
            approvals={approvals}
            badgesByRow={badgesByRow}
            onEdit={id => history.push(`/parsed-decision/${id}`)}
          />

          <ConditionsSection approvals={approvals} conditionsBadges={badgesByRow['conditions'] ?? {}} />
        </Box>
      )}

      {activeTab === 'prescreen' && (
        <Box>
          {prescreenContent ?? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Prescreen results will be available here.
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};
